import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import {
  LinkElementFunction,
  RCButton,
  RCButtonLink,
  RCButtonStandardButtonProps,
} from '../../RCButton/RCButton';
import { ButtonSize, ButtonVariant } from '../../RCButton/styling';
import { ModalActionsAlign } from '../../../styles/generated/data/types';
import { Responsive, responsiveClasses } from '../../helpers/responsive';
import { usePartsContext } from './context';

interface ModalActionSharedProps {
  /** Overrides the default action text. */
  buttonText?: string | JSX.Element;
  disabled?: boolean;
  /** Overrides the slot's default variant (`filled` for primary, `muted` for secondary). */
  variant?: ButtonVariant;
  leadingIcon?: RCButtonStandardButtonProps['leadingIcon'];
  trailingIcon?: RCButtonStandardButtonProps['trailingIcon'];
  /** Defaults to `false`.
   * Optionally focus this action when the modal opens.
   * Note: when using a destructive `primary` action, the `secondary` action should get initial focus.
   */
  initialFocus?: boolean;
}

/** An action that renders a `<button>`. */
export interface RCModalButtonActionProps extends ModalActionSharedProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Makes this action submit the form with this id. The form can live anywhere in the modal. */
  form?: string;
  /** Whether clicking this action closes the modal after its `onClick`.
   * Defaults to `true` for `secondary` (Cancel) and `false` for `primary` and `tertiary`.
   *
   * For async work, leave this off and call `close()` from `useRCModal()` / `useRCDrawer()` once the
   * work succeeds, so the modal stays open if it fails.
   */
  closeOnClick?: boolean;
  renderLink?: never;
}

/** An action that navigates, rendered as an `RCButtonLink`. It never closes the modal: the
 * navigation does the work.
 */
export interface RCModalLinkActionProps extends ModalActionSharedProps {
  /** Renders the action as a link, with the same contract as `RCButtonLink`.
   * For example: `renderLink: c => <CoreLink to="...">{c}</CoreLink>`
   */
  renderLink: LinkElementFunction;
  onClick?: never;
  form?: never;
  closeOnClick?: never;
}

/**
 * An action's own props: a button, or a link when it has `renderLink`. Layout concerns (size, width,
 * alignment) belong to the whole row, so they're set on `RCModalActionsProps` instead.
 */
export type RCModalActionProps = RCModalButtonActionProps | RCModalLinkActionProps;

export type RCModalActionsAlign = ModalActionsAlign;

export interface RCModalActionsProps {
  /** How the actions sit in their row: grouped at the `end`, `split` with `tertiary` on the leading
   * edge and the rest at the end, grouped in the `center`, or `stretch`ed to share the row's full
   * width. Each modal type sets its own default, and passing this replaces it. Responsive: pass a
   * value, or a `{ desktop, tablet, mobile }` object.
   */
  align?: Responsive<RCModalActionsAlign>;
  /** The size of every action's button, so the row stays uniform. Responsive, like RCButton's `size`. */
  size?: Responsive<ButtonSize>;
  /** Defaults to a `filled` button that says 'Save'.
   * Use `true` to render a button with the default properties.
   * Use an object to render a button with overrides (e.g. `{ variant: 'danger', buttonText: 'Delete' }`)
  */
  primary?: true | RCModalActionProps;
  /** Defaults to a `muted` button that says 'Cancel'.
   * Use `true` to render a button with the default properties.
   * Use an object to render a button with overrides (e.g. `{ initialFocus: true }`)
  */
  secondary?: true | RCModalActionProps;
  /** Rarely used to render an accessory action, like going back or deleting.
   * Defaults to a `transparent` button. At `align: 'split'` it sits on the row's leading edge, apart
   * from `primary` and `secondary`.
   */
  tertiary?: RCModalActionProps;
}

type ActionSlot = 'primary' | 'secondary' | 'tertiary';

const SLOT_DEFAULTS: Record<ActionSlot, () => RCModalButtonActionProps> = {
  primary: () => ({
    buttonText: I18n.t('cadence.RCModal.save'),
    closeOnClick: false,
    variant: 'filled',
  }),
  secondary: () => ({
    buttonText: I18n.t('cadence.RCModal.cancel'),
    closeOnClick: true,
    variant: 'muted',
  }),
  tertiary: () => ({
    closeOnClick: false,
    variant: 'transparent',
  }),
};

// One `align` value at a breakpoint → its modifier class, following RCButton's
// `--{modifier}-at-{bp}` convention.
function alignClass(align: RCModalActionsAlign, prefix: string): string {
  // `responsiveClasses` passes '' | 'tablet-' | 'mobile-'; map to the `-at-{bp}` suffix.
  const breakpoint = prefix.replace('-', '');
  const suffix = breakpoint ? `-at-${breakpoint}` : '';
  return `rc-modal-base__actions--${align}${suffix}`;
}

/**
 * The modal's buttons, laid out as one row. The row owns `align` and `size`; each slot owns its own
 * behaviour. Only `secondary` closes the modal by default; see `closeOnClick`. Any slot can be a link
 * by passing `renderLink`.
 */
export function Actions({
  align = undefined,
  size = undefined,
  primary = undefined,
  secondary = undefined,
  tertiary = undefined,
}: RCModalActionsProps) {
  const { modalId, handleOpenChange, setFocusRef } = usePartsContext('Actions');

  const actionRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (actionRef.current) {
      setFocusRef(actionRef);
    }
  }, [setFocusRef, actionRef]);

  function renderAction(slot: ActionSlot, action: true | RCModalActionProps | undefined) {
    if (action === undefined) return null;
    const defaults = SLOT_DEFAULTS[slot]();

    if (action !== true && action.renderLink) {
      const {
        initialFocus,
        buttonText = defaults.buttonText,
        variant = defaults.variant,
        renderLink,
        ...linkProps
      } = action;
      return (
        <RCButtonLink
          {...linkProps}
          id={`${modalId}-action-${slot}`}
          variant={variant}
          data-slot={slot}
          size={size}
          renderLink={renderLink}
          ref={initialFocus ? actionRef as React.RefObject<HTMLAnchorElement> : undefined}
        >
          {buttonText}
        </RCButtonLink>
      );
    }

    const {
      closeOnClick,
      initialFocus,
      buttonText,
      form,
      onClick,
      ...buttonProps
    } = action === true ? defaults : { ...defaults, ...action };

    return (
      <RCButton
        {...buttonProps}
        id={`${modalId}-action-${slot}`}
        data-slot={slot}
        size={size}
        form={form}
        isSubmit={form !== undefined}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(e);
          if (closeOnClick) handleOpenChange(false);
        }}
        ref={initialFocus ? actionRef as React.RefObject<HTMLButtonElement> : undefined}
      >
        {buttonText}
      </RCButton>
    );
  }

  return (
    <div className={classNames('rc-modal-base__actions', responsiveClasses(align, alignClass))}>
      {renderAction('primary', primary)}
      {renderAction('secondary', secondary)}
      {renderAction('tertiary', tertiary)}
    </div>
  );
}
